import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import {
  money,
  PaymentStatusChip,
  SaleStatusChip,
  SALE_STATUS_OPTIONS,
} from "./OwnerCommercialUi";

function emptyLine() {
  return { serviceId: "", quantity: 1, unitPrice: "" };
}

function requestKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `payment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function OwnerSalesSection({ sales, clients, services, accounts, onRefresh }) {
  const activeClients = clients.filter((client) => client.status !== "inactive");
  const activeServices = services.filter((service) => service.active);
  const [form, setForm] = React.useState({ clientId: "", title: "", status: "quoted", area: "Freelancer", notes: "" });
  const [items, setItems] = React.useState([emptyLine()]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [paymentSale, setPaymentSale] = React.useState(null);
  const [payment, setPayment] = React.useState({ accountId: "", amount: "", note: "", requestKey: "" });
  const [paying, setPaying] = React.useState(false);

  const setField = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  function setLine(index, field, value) {
    setItems((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row));
  }

  function chooseService(index, serviceId) {
    const service = activeServices.find((row) => row.id === serviceId);
    setItems((current) => current.map((row, rowIndex) => rowIndex === index ? {
      ...row,
      serviceId,
      unitPrice: service ? String(service.basePrice ?? 0) : "",
    } : row));
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const saleItems = items
        .filter((row) => row.serviceId)
        .map((row) => ({
          serviceId: row.serviceId,
          quantity: Number(row.quantity || 1),
          unitPrice: row.unitPrice === "" ? undefined : Number(row.unitPrice),
        }));
      await ownerApi.createSale({ ...form, items: saleItems });
      setForm({ clientId: "", title: "", status: "quoted", area: "Freelancer", notes: "" });
      setItems([emptyLine()]);
      await onRefresh();
    } catch (err) {
      setError(formatApiError(err, "No se pudo crear la venta"));
    } finally {
      setSaving(false);
    }
  }

  async function changeSaleStatus(sale, status) {
    try {
      await ownerApi.updateSaleStatus(sale.id, status);
      await onRefresh();
    } catch (err) {
      setError(formatApiError(err, "No se pudo actualizar la venta"));
    }
  }

  function openPayment(sale) {
    const compatibleAccount = accounts.find((account) => account.currency === sale.currency && !account.archived);
    setPaymentSale(sale);
    setPayment({
      accountId: compatibleAccount?.id || "",
      amount: String(sale.outstanding || ""),
      note: "",
      requestKey: requestKey(),
    });
  }

  async function submitPayment() {
    if (!paymentSale) return;
    setPaying(true);
    setError("");
    try {
      await ownerApi.createSalePayment(paymentSale.id, {
        accountId: payment.accountId,
        amount: Number(payment.amount),
        note: payment.note,
        requestKey: payment.requestKey,
      });
      setPaymentSale(null);
      await onRefresh();
    } catch (err) {
      setError(formatApiError(err, "No se pudo registrar el cobro"));
    } finally {
      setPaying(false);
    }
  }

  return (
    <Stack spacing={2}>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent component="form" onSubmit={submit}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <PointOfSaleRoundedIcon />
            <Typography variant="h6" sx={{ fontWeight: 950 }}>Nueva venta / trabajo</Typography>
          </Stack>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {!activeClients.length || !activeServices.length ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Primero necesitás al menos un cliente y un servicio activos.
            </Alert>
          ) : null}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))" }, gap: 1.4 }}>
            <TextField required select label="Cliente" value={form.clientId} onChange={setField("clientId")}>
              {activeClients.map((client) => <MenuItem key={client.id} value={client.id}>{client.name}{client.company ? ` · ${client.company}` : ""}</MenuItem>)}
            </TextField>
            <TextField label="Título del trabajo" placeholder="Ej. Rediseño landing agosto" value={form.title} onChange={setField("title")} />
            <TextField select label="Estado de entrega" value={form.status} onChange={setField("status")}>
              {SALE_STATUS_OPTIONS.filter(([value]) => value !== "cancelled").map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField label="Área" value={form.area} onChange={setField("area")} />
          </Box>

          <Typography sx={{ mt: 2.2, mb: 1, fontWeight: 900 }}>Servicios vendidos</Typography>
          <Stack spacing={1.1}>
            {items.map((item, index) => {
              const selected = activeServices.find((service) => service.id === item.serviceId);
              return (
                <Box key={`${index}-${item.serviceId}`} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0,2fr) 110px 160px 42px" }, gap: 1, alignItems: "center" }}>
                  <TextField required select label="Servicio" value={item.serviceId} onChange={(event) => chooseService(index, event.target.value)}>
                    {activeServices.map((service) => <MenuItem key={service.id} value={service.id}>{service.name} · {money(service.basePrice, service.currency)}</MenuItem>)}
                  </TextField>
                  <TextField label="Cant." type="number" inputProps={{ min: .000001, step: "any" }} value={item.quantity} onChange={(event) => setLine(index, "quantity", event.target.value)} />
                  <TextField label={`Precio${selected ? ` ${selected.currency}` : ""}`} type="number" inputProps={{ min: 0, step: "any" }} value={item.unitPrice} onChange={(event) => setLine(index, "unitPrice", event.target.value)} />
                  <IconButton aria-label="Quitar servicio" disabled={items.length === 1} onClick={() => setItems((current) => current.filter((_, rowIndex) => rowIndex !== index))}>
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
          <Button startIcon={<AddRoundedIcon />} onClick={() => setItems((current) => [...current, emptyLine()])} sx={{ mt: 1, textTransform: "none", fontWeight: 850 }}>
            Agregar servicio
          </Button>
          <TextField fullWidth multiline minRows={2} label="Notas de la venta" value={form.notes} onChange={setField("notes")} sx={{ mt: 1.5 }} />
          <Button type="submit" variant="contained" disabled={saving || !activeClients.length || !activeServices.length} sx={{ mt: 2, textTransform: "none", fontWeight: 900 }}>
            {saving ? "Guardando..." : "Crear venta"}
          </Button>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ fontWeight: 950 }}>Ventas y trabajos</Typography>
      <Stack spacing={1.3}>
        {sales.map((sale) => (
          <Card key={sale.id} variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.4} justifyContent="space-between" alignItems={{ md: "flex-start" }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 950 }}>{sale.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sale.client?.name || "Cliente"} · {sale.items?.map((item) => item.serviceName).join(", ")}
                  </Typography>
                  <Stack direction="row" spacing={.8} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <SaleStatusChip status={sale.status} />
                    <PaymentStatusChip status={sale.paymentStatus} />
                  </Stack>
                </Box>
                <Box sx={{ minWidth: { md: 220 }, textAlign: { md: "right" } }}>
                  <Typography sx={{ fontWeight: 950 }}>{money(sale.total, sale.currency)}</Typography>
                  <Typography variant="body2" color="text.secondary">Cobrado {money(sale.paid, sale.currency)}</Typography>
                  <Typography variant="body2" color="text.secondary">Pendiente {money(sale.outstanding, sale.currency)}</Typography>
                </Box>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
                <TextField
                  select
                  size="small"
                  label="Entrega"
                  value={sale.status}
                  onChange={(event) => changeSaleStatus(sale, event.target.value)}
                  sx={{ minWidth: 155 }}
                >
                  {SALE_STATUS_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <Button
                  variant="outlined"
                  startIcon={<PaymentsRoundedIcon />}
                  disabled={sale.status === "cancelled" || sale.outstanding <= 0}
                  onClick={() => openPayment(sale)}
                  sx={{ textTransform: "none", fontWeight: 900 }}
                >
                  Registrar cobro
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
        {!sales.length ? <Typography color="text.secondary">Todavía no registraste ventas.</Typography> : null}
      </Stack>

      <Dialog open={Boolean(paymentSale)} onClose={() => !paying && setPaymentSale(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Registrar cobro</DialogTitle>
        <DialogContent>
          {paymentSale ? (
            <Stack spacing={1.5} sx={{ mt: .5 }}>
              <Alert severity="info">
                {paymentSale.title}: quedan {money(paymentSale.outstanding, paymentSale.currency)} por cobrar.
              </Alert>
              <TextField
                select
                label="Cuenta que recibe el dinero"
                value={payment.accountId}
                onChange={(event) => setPayment((current) => ({ ...current, accountId: event.target.value }))}
              >
                {accounts.filter((account) => account.currency === paymentSale.currency && !account.archived).map((account) => (
                  <MenuItem key={account.id} value={account.id}>{account.name} · {account.currency}</MenuItem>
                ))}
              </TextField>
              <TextField label="Monto" type="number" inputProps={{ min: .000001, max: paymentSale.outstanding, step: "any" }} value={payment.amount} onChange={(event) => setPayment((current) => ({ ...current, amount: event.target.value }))} />
              <TextField label="Nota" value={payment.note} onChange={(event) => setPayment((current) => ({ ...current, note: event.target.value }))} />
              {!accounts.some((account) => account.currency === paymentSale.currency && !account.archived) ? (
                <Alert severity="warning">Creá primero una cuenta de {paymentSale.currency} en Mis Finanzas.</Alert>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentSale(null)} disabled={paying}>Cancelar</Button>
          <Button variant="contained" onClick={submitPayment} disabled={paying || !payment.accountId || !(Number(payment.amount) > 0)}>
            {paying ? "Registrando..." : "Registrar cobro"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
